import {
  Children,
  cloneElement,
  ComponentProps,
  CSSProperties,
  isValidElement,
  type ReactElement,
} from 'react';

import { classNames } from '@/utils/classnames';

import { AvatarProps } from '../Avatar';
import { getAvatarMaskImageData } from '../Avatar/utils';

type AvatarStackProps = ComponentProps<'div'> & {
  /**
   * Size of the avatar components inside the stack.
   */
  size: number;
  /**
   * Defines how much the avatar should overlap each other.
   */
  overlap: number;
  /**
   * Border radius of the avatar components rendered inside the stack.
   * @default 100%;
   */
  borderRadius?: CSSProperties['borderRadius'];
  /**
   * Renders only the specified number of items and appends a badge
   * at the end when the total exceeds this limit.
   */
  maxItems?: number;
  /**
   * Additional classes for the max-items badge.
   */
  badgeClassNames?: string;
};

export const getStackedAvatarStyle = (
  avatarSize: number,
  radius: string | number,
  overlap: number,
) => {
  const maskSize = avatarSize + stackGap * 2;
  const maskRadius = maskSize * toPercentageRadius(radius, avatarSize) + stackGap / 2;

  const maskX = -maskSize + overlap + stackGap;
  const maskY = -stackGap;

  const maskImage = getAvatarMaskImageData(avatarSize, maskSize, maskX, maskY, maskRadius);

  return {
    WebkitMaskImage: maskImage,
    maskImage,
    WebkitMaskMode: 'luminance',
    maskMode: 'luminance',
    marginLeft: -overlap,
  };
};

const toPercentageRadius = (radius: string | number = 0, size: number): number => {
  if (typeof radius === 'number') return (radius * 100) / size;

  const parsed = Number.parseFloat(radius);
  if (Number.isNaN(parsed)) return 0;

  return radius.endsWith('%') ? parsed / 100 : parsed;
};

const stackGap = 2;

export const AvatarStack = (props: AvatarStackProps) => {
  const {
    size,
    overlap,
    borderRadius = '100%',
    maxItems,
    badgeClassNames,
    className,
    children,
    ...otherProps
  } = props;

  const maskStyle = getStackedAvatarStyle(size, borderRadius, overlap);
  // Set a maximum overlap value for the badge to 3px to make sure the badge number is visible
  const badgeMaskStyle = getStackedAvatarStyle(size, borderRadius, Math.min(overlap, 3));

  const childList = Children.toArray(children).filter((child): child is ReactElement<AvatarProps> =>
    isValidElement(child),
  );

  const sliceEndIndex = maxItems != null && childList.length > maxItems ? maxItems - 1 : undefined;
  const filteredChildList = childList.slice(0, sliceEndIndex);

  return (
    <div className={classNames('flex items-center', className)} {...otherProps}>
      {filteredChildList.map((child, index) => {
        const stackedStyle = {
          ...child.props.style,
          zIndex: childList.length - index,
          ...(index > 0 ? maskStyle : undefined),
        };

        return cloneElement(child, { ...child.props, style: stackedStyle });
      })}
      {maxItems != null && childList.length > maxItems && (
        <div
          className={classNames(
            'flex items-center justify-center shadow-15 border-[1px] relative',
            badgeClassNames,
          )}
          style={{ width: size, height: size, borderRadius, ...badgeMaskStyle }}
        >
          <p className="text-[10px] font-medium">{childList.length - maxItems + 1}</p>
        </div>
      )}
    </div>
  );
};
